import {SurveyMediaValue} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {SurveyAnswerDraft} from "../runner/questions/runnerQuestion.types";
import {PublicSurveyIdentity} from "./publicSurvey.api";

/*
    The guest's unsent answers, kept on this device per link, so a reload or a later visit carries on where they
    stopped. Invisible and always on, the same for every link; a storage failure only means there is no draft.
*/

type Answers = Record<string, SurveyAnswerDraft | undefined>;

export type PublicSurveyDraft = {
    answers: Answers
    pageKey?: string
    started: boolean
};

type StoredDraft = PublicSurveyDraft & {
    schema: number
    savedAt: number
    surveyVersion: number
};

const DRAFT_KEY = 'crqlar.survey.draft.';

// Bump it to make every device ignore the drafts it holds, after a change to this shape.
const DRAFT_SCHEMA = 1;

// Counted from the last change. Reminders go out 3 days after the invite by default, and Safari clears a site's
// storage after 7 days without a visit anyway.
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const draftKey = ({token, surveyId}: PublicSurveyIdentity): string | undefined => (
    token ? `${DRAFT_KEY}invitation.${token}` : surveyId ? `${DRAFT_KEY}open.${surveyId}` : undefined
);

const inline = (media?: SurveyMediaValue) => !!media?.mediaURI?.startsWith('data:');

// Only what a reload can bring back: uploads by link (a test link returns files inline, megabytes each) and a
// signature once uploaded (strokes still on the pad are lost). File objects cannot be stored at all.
const storable = (answers: Answers): Answers => Object.fromEntries(Object.entries(answers).map(([questionId, draft]) => {
    if (!draft) return [questionId, draft];

    const signatureValue = inline(draft.signatureValue) ? undefined : draft.signatureValue;
    return [questionId, {
        ...draft,
        mediaValues: draft.mediaValues?.filter(media => !inline(media)),
        localFiles: undefined,
        signatureValue,
        signatureDrawn: draft.signatureDrawn && !!signatureValue,
    }];
}));

const parse = (raw: string | null): StoredDraft | undefined => {
    if (!raw) return undefined;

    try {
        const draft = JSON.parse(raw);
        const readable = draft?.schema === DRAFT_SCHEMA
            && typeof draft.savedAt === 'number'
            && typeof draft.surveyVersion === 'number'
            && !!draft.answers && typeof draft.answers === 'object';
        return readable ? draft : undefined;
    } catch {
        return undefined;
    }
};

const fresh = (draft: StoredDraft | undefined): draft is StoredDraft => (
    !!draft && Date.now() - draft.savedAt < DRAFT_MAX_AGE_MS
);

/** The link's draft if it was made on the loaded version and changed within the last 7 days; any other is removed. */
export const restoreDraft = (link: PublicSurveyIdentity, surveyVersion: number): PublicSurveyDraft | undefined => {
    const key = draftKey(link);
    if (!key) return undefined;

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return undefined;

        const draft = parse(raw);
        if (fresh(draft) && draft.surveyVersion === surveyVersion) {
            return {
                answers: draft.answers,
                pageKey: typeof draft.pageKey === 'string' ? draft.pageKey : undefined,
                started: draft.started === true,
            };
        }

        window.localStorage.removeItem(key);
    } catch {
    }
    return undefined;
};

export const saveDraft = (link: PublicSurveyIdentity, surveyVersion: number, draft: PublicSurveyDraft) => {
    const key = draftKey(link);
    if (!key) return;

    const stored: StoredDraft = {
        schema: DRAFT_SCHEMA,
        savedAt: Date.now(),
        surveyVersion,
        answers: storable(draft.answers),
        pageKey: draft.pageKey,
        started: draft.started,
    };

    try {
        window.localStorage.setItem(key, JSON.stringify(stored));
    } catch {
    }
};

export const dropDraft = (link: PublicSurveyIdentity) => {
    const key = draftKey(link);
    if (!key) return;

    try {
        window.localStorage.removeItem(key);
    } catch {
    }
};

/** localStorage never expires anything itself, so each open removes this device's expired and unreadable drafts. */
export const dropStaleDrafts = () => {
    try {
        const storage = window.localStorage;
        const keys = Array.from({length: storage.length}, (_, index) => storage.key(index))
            .filter((key): key is string => !!key?.startsWith(DRAFT_KEY));

        keys.forEach(key => {
            if (!fresh(parse(storage.getItem(key)))) storage.removeItem(key);
        });
    } catch {
    }
};
